import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Share2, Info, Instagram, Star, Clock, MapPin, X } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { brl, isOpenNow, waNumber, DAY_LABELS, DAY_ORDER, dayIntervals } from "@/lib/format";
import { SmartImage, ViewportVideo } from "@/components/public/media";
import { StoriesStrip, StoryViewer } from "@/components/public/Stories";
import { BestSellers, VideoMenu, Offers } from "@/components/public/Carousels";
import { CatalogItem, CategoryChips } from "@/components/public/Catalog";
import ProductSheet from "@/components/public/ProductSheet";
import CartDrawer from "@/components/public/CartDrawer";

export default function PublicMenu() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [storyIdx, setStoryIdx] = useState(null);
  const [sheetProduct, setSheetProduct] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [infoOpen, setInfoOpen] = useState(false);
  const catRefs = useRef({});

  useEffect(() => {
    api.get(`/public/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiErr(e)))
      .finally(() => setLoading(false));
  }, [slug]);

  const r = data?.restaurant;

  // Apply the restaurant-controlled theme to the whole document (covers portals too).
  useEffect(() => {
    const theme = r?.menu_theme === "light" ? "light" : "dark";
    document.documentElement.setAttribute("data-menu-theme", theme);
    return () => document.documentElement.removeAttribute("data-menu-theme");
  }, [r?.menu_theme]);

  const open = useMemo(() => {
    if (data?.open_now !== undefined) return data.open_now;
    return r ? isOpenNow(r.opening_hours) : false;
  }, [r, data?.open_now]);
  const accepting = r?.accepting_orders !== false;

  const productsByCat = useMemo(() => {
    const map = {};
    (data?.categories || []).forEach((c) => { map[c.id] = []; });
    (data?.products || []).forEach((p) => {
      if (p.available === false) return;
      if (map[p.category_id]) map[p.category_id].push(p);
    });
    return map;
  }, [data]);

  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return (data?.products || []).filter((p) => p.available !== false &&
      (p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)));
  }, [query, data]);

  const addToCart = ({ product, qty, addons, note, unit }) => {
    if (product.sold_out) { toast.error("Produto esgotado"); return; }
    if (!accepting) { toast.warning("Pedidos temporariamente pausados."); }
    const key = `${product.id}|${(addons || []).map((a) => a.itemId).join(",")}|${note || ""}`;
    setCart((prev) => {
      const ex = prev.find((i) => i.key === key);
      if (ex) return prev.map((i) => i.key === key ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { key, product, qty, addons: addons || [], note: note || "", unit }];
    });
    toast.success(`${product.name} adicionado`);
  };

  const quickAdd = (p) => {
    if (p.sold_out) { toast.error("Produto esgotado"); return; }
    if ((p.addon_groups || []).some((g) => g.required)) { setSheetProduct(p); return; }
    addToCart({ product: p, qty: 1, addons: [], note: "", unit: p.promo_price || p.price });
  };

  const setQty = (key, q) => setCart((prev) => q <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => i.key === key ? { ...i, qty: q } : i));
  const removeItem = (key) => setCart((prev) => prev.filter((i) => i.key !== key));

  const selectCat = (id) => {
    setActiveCat(id);
    if (id === "all") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const el = catRefs.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 60, behavior: "smooth" });
  };

  const onStoryCta = (s) => {
    setStoryIdx(null);
    if (s.cta_type === "whatsapp") { window.open(`https://wa.me/${waNumber(r.whatsapp)}`, "_blank"); return; }
    if (["ver_produto", "adicionar"].includes(s.cta_type)) {
      const p = (data.products || []).find((x) => x.id === s.cta_value);
      if (p) { if (s.cta_type === "adicionar") quickAdd(p); else setSheetProduct(p); }
    }
    if (s.cta_type === "ver_categoria" && s.cta_value) selectCat(s.cta_value);
  };

  if (loading) return <LoadingSkeleton />;
  if (err) return <div className="min-h-screen menu-bg flex items-center justify-center menu-muted px-6 text-center">{err}</div>;
  if (data?.blocked) return <BlockedScreen />;

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.unit * i.qty, 0);

  return (
    <div className="max-w-md mx-auto min-h-screen menu-bg menu-text pb-28 relative shadow-2xl border-x menu-border" data-testid="public-menu">
      {/* HERO */}
      <div className="relative h-[340px] overflow-hidden">
        {r.cover_type === "video" && r.cover_video
          ? <ViewportVideo src={r.cover_video} poster={r.cover} priority className="absolute inset-0 h-full w-full object-cover" />
          : <SmartImage src={r.cover} priority className="absolute inset-0 h-full w-full" />}
        {/* blend into page bg + dark scrim for legibility */}
        <div className="absolute inset-0 hero-blend" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 hero-scrim" />

        {/* top row: status left, share/info right */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
          <span data-testid="hero-status" className={`text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur ${open ? "bg-emerald-500/25 text-emerald-200" : "bg-rose-500/25 text-rose-200"}`}>
            {open ? "● Aberto agora" : "● Fechado"}
          </span>
          <div className="flex gap-2">
            <IconBtn tid="hero-share" onClick={() => { navigator.share ? navigator.share({ title: r.name, url: window.location.href }).catch(() => {}) : navigator.clipboard.writeText(window.location.href).then(() => toast.success("Link copiado")); }}><Share2 size={17} /></IconBtn>
            <IconBtn tid="hero-info" onClick={() => setInfoOpen(true)}><Info size={17} /></IconBtn>
          </div>
        </div>

        {/* identity: logo + name + segment/city */}
        <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 z-10 text-white">
          <div className="flex items-end gap-3.5">
            <div className="story-ring p-[2.5px] rounded-2xl shrink-0 shadow-xl">
              <div className="h-[70px] w-[70px] rounded-[14px] overflow-hidden ring-2 ring-black/20">
                <SmartImage src={r.logo} priority className="h-full w-full" />
              </div>
            </div>
            <div className="min-w-0 flex-1 pb-0.5">
              <h1 className="font-head font-extrabold text-white leading-[1.05] line-clamp-2" style={{ fontSize: "clamp(1.3rem, 6.5vw, 1.9rem)" }} data-testid="hero-name">{r.name}</h1>
              <p className="text-xs text-zinc-200/90 mt-1.5 truncate">{r.segment}{r.address?.city ? ` · ${r.address.city}/${r.address.state}` : ""}</p>
            </div>
          </div>
          {/* actions */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {r.whatsapp && <a href={`https://wa.me/${waNumber(r.whatsapp)}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp" className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-full text-white font-bold hmmm-gradient hmmm-glow active:scale-95 transition-transform">Chamar no WhatsApp</a>}
            {r.instagram && <a href={`https://instagram.com/${r.instagram}`} target="_blank" rel="noreferrer" data-testid="hero-instagram" className="flex items-center gap-1.5 text-xs bg-white/15 backdrop-blur px-3.5 py-2 rounded-full text-white font-semibold"><Instagram size={14} /> @{r.instagram}</a>}
          </div>
        </div>
      </div>

      {/* TAGLINE */}
      {r.tagline && <p className="px-6 pt-5 text-center font-head text-base menu-text/90 italic leading-relaxed" data-testid="tagline">“{r.tagline}”</p>}

      {/* INFO CHIPS */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-5 pt-4 pb-0.5">
        {r.rating ? <InfoChip icon={<Star size={13} className="text-amber-400" fill="currentColor" />}>{r.rating}</InfoChip> : null}
        <InfoChip icon={<Clock size={13} />}>{r.avg_time || "—"}</InfoChip>
        <InfoChip>{r.delivery_enabled ? `Entrega ${brl(r.delivery_fee)}` : "Retirada"}</InfoChip>
        {r.min_order ? <InfoChip>Mín. {brl(r.min_order)}</InfoChip> : null}
      </div>

      {!accepting && (
        <div className="mx-5 mt-3 rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 text-center text-sm text-rose-400" data-testid="paused-banner">
          Pedidos temporariamente pausados. Você ainda pode navegar pelo cardápio.
        </div>
      )}

      {accepting && !open && (
        <div className="mx-5 mt-3 rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-center text-sm text-amber-400" data-testid="closed-banner">
          Estabelecimento fechado no momento. Navegue pelo cardápio, mas não é possível finalizar pedidos agora.
        </div>
      )}

      {/* STORIES */}
      <StoriesStrip stories={data.stories} onOpen={setStoryIdx} />

      {/* CAROUSELS */}
      <BestSellers items={data.best_sellers} onOpen={setSheetProduct} onAdd={quickAdd} />
      <VideoMenu items={data.videos} products={data.products} onOpen={setSheetProduct} />
      <Offers items={data.offers} onOpen={setSheetProduct} onAdd={quickAdd} />

      {/* SEARCH */}
      <div className="px-5 pt-6">
        <div className="flex items-center gap-2 rounded-2xl menu-input border menu-border px-4 py-3">
          <Search size={18} className="menu-faint" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="search-input"
            placeholder="Buscar no cardápio..." className="flex-1 bg-transparent outline-none text-sm menu-text placeholder:text-[color:var(--m-faint)]" />
          {query && <button onClick={() => setQuery("")}><X size={16} className="menu-faint" /></button>}
        </div>
      </div>

      {/* CATEGORIES + CATALOG */}
      {!searchResults && (
        <div className="mt-4">
          <CategoryChips categories={data.categories} active={activeCat} onSelect={selectCat} />
          <div className="mt-4 space-y-8">
            {data.categories.filter((c) => c.active && (productsByCat[c.id] || []).length).map((c) => (
              <div key={c.id} ref={(el) => (catRefs.current[c.id] = el)} data-testid={`catalog-cat-${c.id}`}>
                <div className="px-5 mb-2">
                  <h2 className="font-head text-xl font-extrabold menu-text">{c.name} <span className="text-sm menu-faint font-medium">· {productsByCat[c.id].length}</span></h2>
                  {c.subtitle && <p className="text-xs menu-faint">{c.subtitle}</p>}
                </div>
                <div className="px-5 space-y-3">
                  {productsByCat[c.id].map((p) => <CatalogItem key={p.id} p={p} onOpen={setSheetProduct} onAdd={quickAdd} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {searchResults && (
        <div className="px-5 mt-4 space-y-3" data-testid="search-results">
          {searchResults.length === 0 && <p className="text-center menu-faint py-10">Nenhum item encontrado.</p>}
          {searchResults.map((p) => <CatalogItem key={p.id} p={p} onOpen={setSheetProduct} onAdd={quickAdd} />)}
        </div>
      )}

      {/* STORE INFO */}
      <StoreInfo r={r} open={open} />

      <footer className="px-5 mt-8 py-6 text-center text-xs menu-faint border-t menu-border">
        <p className="font-head font-bold menu-muted">{r.name}</p>
        <p className="mt-2 text-[11px]">Criado com <span className="hmmm-text font-bold">HMMM MENU</span></p>
        <p className="mt-1 menu-faint italic">HMMM. — o som do desejo antes do pedido.</p>
      </footer>

      {/* CART BAR */}
      {cartCount > 0 && (
        <button onClick={() => setCartOpen(true)} data-testid="cart-bar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-[26rem] rounded-full hmmm-gradient text-white px-5 py-4 font-bold flex items-center justify-between hmmm-glow active:scale-[0.98] transition-transform">
          <span className="text-sm">{cartCount} {cartCount === 1 ? "item" : "itens"}</span>
          <span className="text-sm">Ver carrinho</span>
          <span className="font-head font-extrabold">{brl(cartTotal)}</span>
        </button>
      )}

      {storyIdx !== null && <StoryViewer stories={data.stories} index={storyIdx} onClose={() => setStoryIdx(null)} onCta={onStoryCta} />}
      <ProductSheet product={sheetProduct} products={data.products} open={!!sheetProduct} onClose={() => setSheetProduct(null)} onAdd={addToCart} />
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} items={cart} restaurant={r} products={data.products}
        setQty={setQty} removeItem={removeItem} addUpsell={(u) => quickAdd(u)}
        onOrderPlaced={() => { setCart([]); setCartOpen(false); }} />

      {infoOpen && (
        <div className="fixed inset-0 z-[75] bg-black/70 flex items-end sm:items-center justify-center" onClick={() => setInfoOpen(false)}>
          <div className="menu-surface menu-text w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 border menu-border" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head text-xl font-extrabold mb-2">{r.name}</h3>
            <p className="text-sm menu-muted">{r.description}</p>
            <button onClick={() => setInfoOpen(false)} className="mt-5 w-full rounded-full menu-chip py-3 font-bold menu-text">Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StoreInfo({ r, open }) {
  const [expanded, setExpanded] = useState(false);
  const JS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const todayKey = JS[new Date().getDay()];
  const today = r.opening_hours?.[todayKey];
  const todayIvs = dayIntervals(today);
  const fmtDay = (day) => { const ivs = dayIntervals(day); return ivs.length ? ivs.map((i) => `${i.open}–${i.close}`).join(" • ") : "Fechado"; };
  const addr = [r.address?.street, r.address?.number, r.address?.neighborhood, r.address?.city].filter(Boolean).join(", ");
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || r.name)}`;
  return (
    <div className="px-5 mt-10">
      <div className="rounded-2xl menu-surface border menu-border p-5" data-testid="store-info-card">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${open ? "bg-emerald-400" : "bg-rose-400"}`} />
          <span className="font-head font-bold menu-text">{open ? "Aberto agora" : "Fechado agora"}</span>
          {open && todayIvs.length > 0 && <span className="text-sm menu-muted">• Fecha às {todayIvs[todayIvs.length - 1].close}</span>}
        </div>
        {addr && (
          <div className="flex items-start justify-between gap-3 mt-3">
            <p className="text-sm menu-muted flex items-start gap-2"><MapPin size={15} className="mt-0.5 shrink-0" />{addr}</p>
            <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="store-map" className="text-xs font-semibold hmmm-text whitespace-nowrap">Ver no mapa</a>
          </div>
        )}
        <div className="mt-3 pt-3 border-t menu-border">
          <div className="flex items-center justify-between">
            <span className="text-sm menu-muted">Hoje • {fmtDay(today)}</span>
            <button onClick={() => setExpanded(!expanded)} data-testid="store-hours-toggle" className="text-xs font-semibold menu-muted">{expanded ? "Ocultar" : "Ver todos os horários"}</button>
          </div>
          {expanded && (
            <div className="mt-3 space-y-1.5" data-testid="store-hours-full">
              {DAY_ORDER.map((d) => {
                const h = r.opening_hours?.[d];
                return <div key={d} className={`flex justify-between gap-3 text-xs ${d === todayKey ? "menu-text font-semibold" : "menu-faint"}`}><span className="shrink-0">{DAY_LABELS[d]}</span><span className="text-right">{fmtDay(h)}</span></div>;
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BlockedScreen() {
  return (
    <div className="min-h-screen bg-[#0C0C0E] text-zinc-100 flex flex-col items-center justify-center px-8 text-center" data-testid="menu-blocked">
      <img src="/hmmm-icon.png" alt="" className="h-16 w-16 opacity-80 mb-6" />
      <h1 className="font-head text-2xl font-extrabold leading-tight">Cardápio temporariamente indisponível</h1>
      <p className="mt-3 text-sm text-zinc-400 max-w-xs">Este estabelecimento está com o acesso ao cardápio temporariamente suspenso.</p>
      <p className="mt-10 text-[11px] text-zinc-600">Criado com <span className="hmmm-text font-bold">HMMM MENU</span></p>
    </div>
  );
}

function IconBtn({ children, onClick, tid }) {
  return <button onClick={onClick} data-testid={tid} className="h-9 w-9 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white">{children}</button>;
}
function InfoChip({ icon, children }) {
  return <span className="shrink-0 flex items-center gap-1.5 text-xs font-semibold menu-chip border menu-border px-3 py-2 rounded-full menu-text">{icon}{children}</span>;
}
function LoadingSkeleton() {
  return (
    <div className="max-w-md mx-auto min-h-screen menu-bg">
      <div className="h-[340px] skeleton" />
      <div className="p-5 space-y-3">
        <div className="h-6 w-1/2 skeleton rounded" />
        <div className="flex gap-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-[92px] w-[72px] skeleton rounded-2xl" />)}</div>
        <div className="h-40 skeleton rounded-2xl mt-4" />
      </div>
    </div>
  );
}
